'use client';
import { useState, useEffect, useCallback } from 'react';
import Subsection from 'components/containers/sub-section';
import SearchLayout from 'components/search/search-layout';
import CollapsibleDrawer from 'components/collapsible-drawer';
import CheckboxGroupHeading from 'components/controls/checkbox-group-heading';
import CheckboxWithLabel from 'components/controls/checkbox-with-label';
import FlexContainer from 'components/containers/flex-container';
import ClearableFilters from 'components/controls/filters/clearable-filters';
import DateFilter from 'components/controls/filters/date-filter';
import styles from './events-and-tours-app.module.scss';
import { useRouter, usePathname } from 'src/navigation';
import { cleanURLParams } from 'helpers/search/cleanURLParams';
import type { EventsAndToursAppProps } from 'types/events-and-tours/types';
import { getAceSearchString } from 'helpers/ACE/rosetta';
import { newYorkizeDate } from 'helpers/date-time';
import { startOfDay } from 'date-fns';
import ToursDisplay from './toursDisplay';
import EventsDisplay from './eventsDisplay';
import { type EventsApiResponse, eventSearch } from 'lib/search/eventSearch';
import { tourSearch } from 'lib/search/tourSearch';
import { ToggleButton } from 'components/controls/buttons/toggle-button';

const ARRAY_PARAMS = ['audience', 'location', 'price', 'type', 'program'];
const deserializeParams = (params) => {
	Object.keys(params).forEach((key) => {
		if (ARRAY_PARAMS.includes(key)) {
			params[key] = `${params[key]}`.split(',');
		}
	});
	return params;
};

const totalText = (resultTotal: number, total: number): string => {
	return total == 0 ? '0 results' : `${resultTotal} of ${total} results`;
};

const isEventsResponse = (response: any): response is EventsApiResponse => {
	return !!(response as EventsApiResponse).events;
};

const dateRegex = /\d{4}-\d{2}-\d{2}$/;

const urlParamToDate = (date: string): Date | undefined => {
	if (!date) {
		return undefined;
	}

	if (!dateRegex.test(date)) {
		return undefined;
	}

	return newYorkizeDate(date);
};

const cleanParamsForUrl = (params) => {
	const cleanedParams = cleanURLParams(params);
	Object.keys(cleanedParams).forEach((key) => {
		if (key == 'locale') {
			delete cleanedParams[key];
		}
	});
	return cleanedParams;
};

const EventsAndToursApp = ({
	results,
	filterGroups,
	total,
	aggs,
	searchParams,
	type,
}: EventsAndToursAppProps) => {
	const pathname = usePathname();
	const router = useRouter();
	const [params, setParams] = useState(deserializeParams(searchParams));
	const [backButton, setBackButton] = useState({
		name: 'Previous Week',
		value: '0',
		disabled: true,
	});
	const [forwardButton, setForwardButton] = useState({
		name: 'Next Week',
		value: '2',
	});
	const [startDate, setStartDate] = useState(urlParamToDate(params.from));
	const [endDate, setEndDate] = useState(
		urlParamToDate(params.to) || urlParamToDate(params.from),
	);
	const [page, setPage] = useState(1); // only used for Read More for events
	const [resultItems, setResultItems] = useState(results);
	const [totalResults, setTotalResults] = useState(total);

	const todayInNewYork = startOfDay(newYorkizeDate(new Date().toISOString()));

	const fetchResults = useCallback(
		async (params) => {
			if (type == 'tours') {
				return tourSearch({
					...params,
					audience: params.audience?.join(',') || '',
					location: params.location?.join(',') || '',
					type: params.type?.join(',') || '',
					price: params.price?.join(',') || '',
					program: params.program?.join(',') || '',
				});
			} else {
				return eventSearch({
					...params,
					audience: params.audience?.join(',') || '',
					location: params.location?.join(',') || '',
					type: params.type?.join(',') || '',
					price: params.price?.join(',') || '',
					program: params.program?.join(',') || '',
					page: params.page,
				});
			}
		},
		[type],
	);

	const aggsLookup = Object.keys(aggs.aggregations)
		.flatMap((key) => {
			return aggs.aggregations[key].buckets;
		})
		.filter((item) => !!item.key); // remove blanks

	const getCountByName = (name) => {
		const item = aggsLookup.find((agg) => agg.key == getAceSearchString(name));
		return item ? item.doc_count : 0;
	};

	const isChecked = (name) => {
		return !!Object.keys(params).find((param) => params[param]?.includes(name));
	};

	// the filterGroups array is the cornerstone of finding where any one parameter lives

	const someSubFiltersAreSelected = (groupName, subFilters = []) => {
		const filterNames = subFilters.map((filter) => filter.name);
		const paramGroup = params[groupName] || [];
		return (
			paramGroup.some((name) => filterNames.includes(name)) &&
			!filterNames.every((name) => paramGroup.includes(name))
		);
	};

	const filterHasChildren = (name) => {
		const filterParent = filterGroups.find((filter) =>
			filter.filters.some((item) => item.name == name),
		);
		return (
			!!filterParent &&
			!!filterParent.filters.find((item) => item.name == name)?.subFilters
				?.length
		);
	};

	const filterChildren = (name) => {
		const filterParent = filterGroups.find((filter) =>
			filter.filters.some((item) => item.name == name),
		);
		return filterParent
			? filterParent.filters
					.find((item) => item.name == name)
					.subFilters?.map((item) => item.name) || []
			: [];
	};

	const filterGroupCount = (filters = []) => {
		const filterNames = filters.map((filter) => filter.name);
		return filterNames.reduce((total, filterName) => {
			return total + (isChecked(filterName) ? 1 : 0);
		}, 0);
	};

	const filterCount = (filters) => {
		return filters.reduce((total, filter) => {
			return (
				total +
				(isChecked(filter.name) ? 1 : 0) +
				filterGroupCount(filter.subFilters)
			);
		}, 0);
	};

	const getFilterGroupByFilterName = (name) => {
		return filterGroups.find((group) => {
			return group.filters.some((filter) => {
				return (
					filter.name == name ||
					(!!filter.subFilters &&
						filter.subFilters?.some((subFilter) => subFilter.name == name))
				);
			});
		}).name;
	};

	const getFilterTextFromValue = (name: string, facetName: string = '') => {
		// :`( special case for program filters, which are the ACE program names not in rosetta
		// those names are input by hand in email blasts sent by marketing
		if (facetName == 'program' && name.length > 2) {
			return name.replaceAll('|', ' - ');
		}

		const text = filterGroups.reduce((currentText, filterGroup) => {
			const filterText = filterGroup.filters.reduce(
				(currentFilterText, filter) => {
					if (filter.name == name) {
						return filter.text;
					}
					if (filter.subFilters?.length) {
						const subFilter = filter.subFilters.find((sub) => sub.name == name);
						if (subFilter) {
							return subFilter.text;
						}
					}
					return currentFilterText;
				},
				'',
			);
			return filterText ? filterText : currentText;
		}, '');
		return text;
	};

	const toggleValueInParamArray = (paramArray, paramName) => {
		if (!isChecked(paramName)) {
			paramArray?.push(paramName);
		} else {
			// remove
			const index = paramArray?.indexOf(paramName);
			paramArray?.splice(index, 1);
		}
		return paramArray;
	};

	const mapParamsToClearableFilters = () => {
		const result = Object.keys(params)
			.map((facetName) => {
				if (Array.isArray(params[facetName])) {
					return {
						name: facetName,
						selectedValues: params[facetName].map((value) => ({
							label: getFilterTextFromValue(value, facetName),
							value,
							facet: facetName,
						})),
					};
				}
			})
			.filter(Boolean);

		return result;
	};

	const isProgramFilter = (value) => {
		if (params['program']?.length) {
			return params['program'].includes(value);
		}
		return false;
	};

	const clearProgramFilter = (value) => {
		const newParams = { ...params };
		const programParams = params['program'] || [];
		const newProgramParams = programParams.filter(
			(program) => program != value,
		);
		setParams({
			...newParams,
			program: newProgramParams.length ? newProgramParams : undefined,
		});
		setPage(1);
	};

	const handleClearOne = (event) => {
		const { value } = event.target;

		if (isProgramFilter(value)) {
			clearProgramFilter(value);
		} else {
			toggleParam(value);
		}
		setPage(1);
	};

	const handleClearAll = () => {
		setParams({});
		setPage(1);
	};

	const handleCheckboxChange = (e: MouseEvent | KeyboardEvent) => {
		const { value } = e.target as HTMLButtonElement;
		toggleParam(value);
		setPage(1);
	};

	const toggleParam = (paramName: string) => {
		const isBeingUnset = isChecked(paramName);

		const newParams = { ...params }; //TODO: set date back to today
		const groupName = getFilterGroupByFilterName(paramName);
		let currentValues = newParams?.[groupName] || [];

		currentValues = toggleValueInParamArray(currentValues, paramName);

		if (filterHasChildren(paramName)) {
			if (isBeingUnset) {
				filterChildren(paramName).forEach((name) => {
					if (isChecked(name)) {
						currentValues = toggleValueInParamArray(currentValues, name);
					}
				});
			} else {
				filterChildren(paramName).forEach((name) => {
					if (!isChecked(name)) {
						currentValues = toggleValueInParamArray(currentValues, name);
					}
				});
			}
		}

		newParams[groupName] = currentValues;

		setParams(newParams);
		setPage(1);
	};

	const handlePageChange = (event) => {
		const { value = '1' } = event.target;
		const newParams = { ...params };
		if (value == '1') {
			delete newParams.page;
		} else {
			newParams.page = value;
		}

		setBackButton({
			name: 'Previous Week',
			value: `${parseInt(value) - 1}`,
			disabled: value == '1',
		});

		setForwardButton({
			name: 'Next Week',
			value: `${parseInt(value) + 1}`,
		});

		setParams(newParams);
		window.scrollTo(0, 0);
	};

	const handleLoadMore = async () => {
		const moreResults = (await fetchResults({
			...params,
			page: page + 1,
		})) as EventsApiResponse;
		setResultItems([...resultItems, ...moreResults.events]);
		setPage(page + 1);
	};

	const onCalendarChange = (dates) => {
		const [start, end] = dates;
		const newParams = { ...params };
		delete newParams.page; // Page and date conflict as date navigation

		setStartDate(start);
		setEndDate(end);

		if (start) {
			newParams.from = start.toISOString().substring(0, 10);
		} else {
			delete newParams.from;
		}
		if (end) {
			newParams.to = end.toISOString().substring(0, 10);
		} else {
			delete newParams.to;
		}

		setParams(newParams);
		setPage(1);
	};

	useEffect(() => {
		const goFish = async () => {
			const results = await fetchResults(params);
			if (isEventsResponse(results)) {
				setResultItems(results.events);
			} else {
				setResultItems(results.tours);
			}
			setTotalResults(results.total as number);
		};
		goFish();

		router.push(
			`${pathname}?${new URLSearchParams(cleanParamsForUrl(params))}`,
			{
				scroll: false,
			},
		);
	}, [params, pathname, router, fetchResults]);

	return (
		<div className={styles.container}>
			<Subsection className={styles.subsection}>
				<SearchLayout inverted={true}>
					{/* main view */}
					<div>
						{mapParamsToClearableFilters().length > 0 && (
							<FlexContainer
								gap="var(--spacing-micro)"
								wrap="wrap"
								className={styles.pills}
							>
								<ClearableFilters
									groupName="events-and-tours-facets"
									handleClearAll={handleClearAll}
									handleClose={handleClearOne}
									facets={mapParamsToClearableFilters()}
								/>
							</FlexContainer>
						)}
						<div className={styles.resultsCountSection}>
							<div className={styles.resultCount}>
								{totalText(resultItems.length, totalResults)}
							</div>
							<DateFilter
								startDate={startDate}
								endDate={endDate}
								minDate={todayInNewYork}
								onCalendarChange={onCalendarChange}
							/>
						</div>

						{type === 'tours' && (
							<ToursDisplay
								results={resultItems}
								startDate={startDate}
								endDate={endDate}
								handlePageChange={handlePageChange}
								backButton={backButton}
								forwardButton={forwardButton}
							/>
						)}

						{type === 'events' && <EventsDisplay results={resultItems} />}
					</div>

					{/* filter bar */}
					<div>
						<div className={styles.filterHeading}>
							<h4>Narrow your Search</h4>
						</div>
						{filterGroups.map((group) => {
							const groupName = group.name;
							return (
								<CollapsibleDrawer
									open={false}
									key={group.name}
									heading={
										<CheckboxGroupHeading
											name={group.text}
											count={filterCount(group.filters)}
										/>
									}
								>
									<div className={styles.checkboxGroups}>
										{group.filters.map((filter) => {
											const { text, name, subFilters } = filter;
											const parentName = name;
											const checked = isChecked(name);
											const indeterminate =
												!!subFilters?.length &&
												someSubFiltersAreSelected(groupName, subFilters);
											return (
												<div key={`group${name}`}>
													<CheckboxWithLabel
														checked={checked}
														indeterminate={indeterminate}
														text={text}
														name={name}
														key={text + name + checked}
														id={`${group.name}:${name}`}
														value={name}
														onChange={handleCheckboxChange}
														count={getCountByName(name)}
														disabled={getCountByName(name) == 0}
													/>
													{!!subFilters?.length && (
														<fieldset className={styles.checkboxSubGroups}>
															{subFilters.map((subfilter) => {
																const { text, name } = subfilter;
																const isLongText = text.length > 22;
																const checked = isChecked(name);
																return (
																	<CheckboxWithLabel
																		checked={checked}
																		text={text}
																		name={name}
																		value={name}
																		key={`${parentName}-${name}-${checked}`}
																		id={`${group.name}:${name}:${parentName}`}
																		onChange={handleCheckboxChange}
																		count={getCountByName(name)}
																		disabled={getCountByName(name) == 0}
																		alignCheckboxToTop={isLongText}
																	/>
																);
															})}
														</fieldset>
													)}
												</div>
											);
										})}
									</div>
								</CollapsibleDrawer>
							);
						})}
					</div>
				</SearchLayout>
			</Subsection>
			<Subsection className={styles.loadMoreSection}>
				<div className={styles.loadMoreContainer}>
					<div className={styles.totalResults}>
						Showing {resultItems.length} of {totalResults} results
					</div>
					{!!resultItems?.length && resultItems?.length < totalResults && (
						<ToggleButton
							tabIndex={0}
							onClick={handleLoadMore}
							text={'Load more'}
						/>
					)}
				</div>
			</Subsection>
		</div>
	);
};

export default EventsAndToursApp;
